import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { dedupeCollectionCases } from '../utils/loandiskId';
import { 
  Box, 
  Paper, 
  Typography, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import BorrowerSearchAutocomplete from '../components/BorrowerSearchAutocomplete';

// Styled table cell for header cells/styles
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5),
  whiteSpace: 'nowrap'
}));

// Add styled table row for hover effect
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& td': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  }
}));

// Define the shape of a case record
interface CollectionCase {
  id: string;
  account_balance: number;
  status: string;
  loandisk_id: string;
  agent_id: string | null;
  borrower: {
    name: string;
    mobile_no: string;
    email: string;
    address: string;
  };
}

// Raw type from Supabase (borrower comes back as an array)
interface RawCollectionData {
  id: string;
  account_balance: number;
  status: string;
  loandisk_id: string | null;
  agent_id: string | null;
  borrower: {
    name: string;
    mobile_no: string;
    email: string;
    address: string;
  } | {
    name: string;
    mobile_no: string;
    email: string;
    address: string;
  }[];
}

const AgentBorrowerList: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [cases, setCases] = useState<CollectionCase[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedBorrowerId, setSelectedBorrowerId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchCases = useCallback(async () => {
    try {
      setLoading(true);
      if (!userProfile?.id) return;

      const { data, error } = await supabase
        .from('for_collection')
        .select(`
          id,
          account_balance,
          status,
          loandisk_id,
          agent_id,
          borrower:borrowers!inner(
            name,
            mobile_no,
            email,
            address
          )
        `)
        .eq('agent_id', userProfile.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching cases:', error);
        return;
      }

      if (data) {
        const transformedData: CollectionCase[] = (data as RawCollectionData[]).map(item => ({
          id: item.id,
          account_balance: Number(item.account_balance),
          status: item.status,
          loandisk_id: item.loandisk_id || '',
          agent_id: item.agent_id,
          // borrower comes as an array, so pick the first element
          borrower: Array.isArray(item.borrower)
            ? {
                name: item.borrower[0].name,
                mobile_no: item.borrower[0].mobile_no,
                email: item.borrower[0].email,
                address: item.borrower[0].address,
              }
            : item.borrower
        }));
        setCases(dedupeCollectionCases(transformedData));
      }
    } catch (err) {
      console.error('Error in fetchCases:', err);
    } finally {
      setLoading(false);
    }
  }, [userProfile]);

  useEffect(() => {
    fetchCases();
  }, [userProfile, fetchCases]);

  // Handle refreshing the data
  const handleRefresh = () => {
    fetchCases();
  };

  // Handle clearing all filters
  const handleClearFilters = () => {
    setSelectedBorrowerId(null);
    setSelectedStatus('');
    setPage(0);
  };

  // Filter cases based on selected borrower and status
  const filteredCases = cases.filter(caseItem => {
    const matchesBorrower = selectedBorrowerId ? caseItem.id === selectedBorrowerId : true;
    const matchesStatus = selectedStatus ? caseItem.status === selectedStatus : true;
    return matchesBorrower && matchesStatus;
  });

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get color for status chip
  const getStatusChipColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'for collection':
        return {
          bg: '#ffebee',
          color: '#d32f2f'
        };
      case 'for ci':
        return {
          bg: '#e3f2fd',
          color: '#1976d2'
        };
      case 'active':
        return {
          bg: '#e8f5e9',
          color: '#2e7d32'
        };
      default:
        return {
          bg: '#f5f5f5',
          color: '#757575'
        };
    }
  };

  // Get unique statuses from cases
  const uniqueStatuses = Array.from(new Set(cases.map(caseItem => caseItem.status)))
    .filter(status => !!status);

  // Add handler for row clicks
  const handleRowClick = (borrowerId: string) => {
    navigate(`/borrower/${borrowerId}`);
  };

  if (!userProfile) {
    return <Alert severity="error">Please log in to view cases.</Alert>;
  }

  const isFiltered = selectedBorrowerId !== null || selectedStatus !== '';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 2, sm: 4 } }}>
      <Container maxWidth="md" sx={{ px: { xs: 1.5, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 }, gap: 1 }}>
          <Typography 
            variant={isMobile ? "h4" : "h3"}
            sx={{ 
              fontWeight: 800,
              mb: 1,
              background: 'linear-gradient(45deg, #1976d2, #64b5f6)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              fontSize: isMobile ? '1.75rem' : '2.5rem',
              lineHeight: isMobile ? 1.2 : 1.3,
            }}
          >
            My Borrowers
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isFiltered && (
              <Button 
                startIcon={<ClearIcon />} 
                variant="outlined" 
                onClick={handleClearFilters}
                size="small"
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </Box>

        {/* Filter controls */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <BorrowerSearchAutocomplete
              onBorrowerSelect={setSelectedBorrowerId}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Filter by Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={selectedStatus}
                label="Filter by Status"
                onChange={(e) => setSelectedStatus(e.target.value as string)}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }
                }}
              >
                <MenuItem value="">
                  <em>All Status</em>
                </MenuItem>
                {uniqueStatuses.map((status) => (
                  <MenuItem
                    key={status}
                    value={status}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Chip
                      label={status}
                      size="small"
                      sx={{
                        backgroundColor: getStatusChipColor(status).bg,
                        color: getStatusChipColor(status).color,
                        fontWeight: 500,
                        borderRadius: '16px'
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Paper sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredCases.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              {cases.length === 0 ? 'No borrowers found.' : 'No borrowers match the current filters.'}
            </Alert>
          ) : isMobile ? (
            <>
              {/* Mobile: card list so agents in the field still see phone + address with tap-to-call */}
              <Stack spacing={0} sx={{ p: 1 }}>
                {(rowsPerPage > 0
                  ? filteredCases.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : filteredCases
                ).map(caseItem => (
                  <Card
                    key={caseItem.id}
                    variant="outlined"
                    sx={{ mb: 1, borderRadius: 2, '&:last-of-type': { mb: 0 } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                      <CardActionArea onClick={() => handleRowClick(caseItem.id)} sx={{ flex: 1 }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ minWidth: 0 }}>
                              {caseItem.borrower.name || 'N/A'}
                            </Typography>
                            <Chip
                              label={caseItem.status}
                              size="small"
                              sx={{
                                flexShrink: 0,
                                backgroundColor: getStatusChipColor(caseItem.status).bg,
                                color: getStatusChipColor(caseItem.status).color,
                                fontWeight: 500,
                                borderRadius: '16px',
                              }}
                            />
                          </Box>
                          {caseItem.borrower.address && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, color: 'text.secondary' }}>
                              <LocationIcon sx={{ fontSize: 16, mt: '2px', flexShrink: 0 }} />
                              <Typography variant="body2" color="text.secondary">
                                {caseItem.borrower.address}
                              </Typography>
                            </Box>
                          )}
                          {caseItem.borrower.mobile_no && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                              {caseItem.borrower.mobile_no}
                            </Typography>
                          )}
                        </CardContent>
                      </CardActionArea>
                      {caseItem.borrower.mobile_no && (
                        <Tooltip title={`Call ${caseItem.borrower.mobile_no}`}>
                          <IconButton
                            component="a"
                            href={`tel:${caseItem.borrower.mobile_no}`}
                            onClick={(e) => e.stopPropagation()}
                            color="primary"
                            sx={{ alignSelf: 'center', mr: 0.5 }}
                          >
                            <PhoneIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Card>
                ))}
              </Stack>

              <TablePagination
                component="div"
                count={filteredCases.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Borrower Name</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                      <StyledTableCell>Mobile No.</StyledTableCell>
                      <StyledTableCell>Address</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(rowsPerPage > 0
                      ? filteredCases.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      : filteredCases
                    ).map(caseItem => (
                      <StyledTableRow
                        key={caseItem.id}
                        onClick={() => handleRowClick(caseItem.id)}
                        hover
                      >
                        <TableCell>{caseItem.borrower.name || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={caseItem.status}
                            size="small"
                            sx={{
                              backgroundColor: getStatusChipColor(caseItem.status).bg,
                              color: getStatusChipColor(caseItem.status).color,
                              fontWeight: 500,
                              borderRadius: '16px'
                            }}
                          />
                        </TableCell>
                        <TableCell>{caseItem.borrower.mobile_no || 'N/A'}</TableCell>
                        <TableCell>{caseItem.borrower.address || 'N/A'}</TableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filteredCases.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AgentBorrowerList;
